import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { relationshipService, memberService } from '../../services/api';
import { FiHeart, FiPlus, FiTrash2, FiX, FiSearch } from 'react-icons/fi';
import './Relationships.css';

const Relationships = ({ memberId, isOwnProfile, canManage: canManageProp, isManagingProfile = false }) => {
  const { user } = useAuth();
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchRelationships();
    // eslint-disable-next-line
  }, [memberId]);

  const fetchRelationships = async () => {
    try {
      setLoading(true);
      const response = await relationshipService.getMemberRelationships(memberId);
      if (response.success) {
        setRelationships(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch relationships:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (relationshipId) => {
    if (!window.confirm('Remove this relationship?')) return;
    try {
      const response = await relationshipService.deleteRelationship(relationshipId);
      if (response.success) {
        setRelationships(prev => prev.filter(r => r.relationshipID !== relationshipId));
      }
    } catch (err) {
      console.error('Failed to delete relationship:', err);
    }
  };

  const handleRelationshipAdded = (newRelationship) => {
    setRelationships(prev => [...prev, newRelationship]);
    setShowAddModal(false);
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // Group relationships by category
  const grouped = relationships.reduce((acc, rel) => {
    const category = rel.relationshipName || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(rel);
    return acc;
  }, {});

  const canManage = canManageProp !== undefined ? canManageProp : (isOwnProfile || user?.isAdmin);

  if (loading) {
    return (
      <div className="loading-state">
        <span className="spinner"></span>
        <p>Loading relationships...</p>
      </div>
    );
  }

  return (
    <div className="relationships-section">
      <div className="relationships-header">
        <h3><FiHeart /> Family Relationships</h3>
        {canManage && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)}>
            <FiPlus /> Add Relationship
          </button>
        )}
      </div>

      {relationships.length === 0 ? (
        <div className="empty-state card">
          <div className="empty-state-icon">👨‍👩‍👧‍👦</div>
          <h3 className="empty-state-title">No relationships yet</h3>
          <p>{isOwnProfile ? 'Add your family connections!' : 'No family connections added yet.'}</p>
        </div>
      ) : (
        <div className="relationships-list">
          {Object.entries(grouped).map(([category, rels]) => (
            <div key={category} className="relationship-group">
              {rels.map(rel => (
                <div key={rel.relationshipID} className="relationship-card card">
                  <Link to={`/member/${rel.relatedMember?.memberID}`} className="relationship-member">
                    {rel.relatedMember?.profilePhotoURL ? (
                      <img
                        src={rel.relatedMember.profilePhotoURL}
                        alt={rel.relatedMember.fullName}
                        className="avatar"
                      />
                    ) : (
                      <div className="avatar">
                        {getInitials(rel.relatedMember?.firstName, rel.relatedMember?.lastName)}
                      </div>
                    )}
                    <div className="relationship-info">
                      <span className="relationship-name">{rel.relatedMember?.fullName}</span>
                      <span className="relationship-type">{rel.relationshipName}</span>
                    </div>
                  </Link>
                  {canManage && (
                    <button
                      className="btn btn-ghost btn-sm relationship-delete"
                      onClick={() => handleDelete(rel.relationshipID)}
                      title="Remove relationship"
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddRelationshipModal
          onClose={() => setShowAddModal(false)}
          onAdded={handleRelationshipAdded}
          existingRelationships={relationships}
          profileMemberId={memberId}
          currentUserId={user?.memberID}
          isManagingProfile={isManagingProfile}
        />
      )}
    </div>
  );
};

// ============================================
// ADD RELATIONSHIP MODAL
// ============================================
const AddRelationshipModal = ({ onClose, onAdded, existingRelationships, profileMemberId, currentUserId, isManagingProfile }) => {
  const isViewingSomeoneElse = profileMemberId && profileMemberId !== currentUserId;
  // Skip search when viewing someone else's profile UNLESS we're managing it.
  // (Regular member on someone's profile: the target is obviously that person.)
  // (Manager on a managed profile: we're adding relationships FOR them, so we search.)
  const shouldSkipSearch = isViewingSomeoneElse && !isManagingProfile;

  const [step, setStep] = useState(shouldSkipSearch ? 2 : 1);
  const [preSelectedMember, setPreSelectedMember] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [relationshipTypes, setRelationshipTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRelationshipTypes();
    if (isViewingSomeoneElse) {
      loadProfileMember();
    }
    // eslint-disable-next-line
  }, []);

  const fetchRelationshipTypes = async () => {
    try {
      const response = await relationshipService.getRelationshipTypes();
      if (response.success) {
        setRelationshipTypes(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch relationship types:', err);
    }
  };

  const loadProfileMember = async () => {
    try {
      const response = await memberService.getMember(profileMemberId);
      if (response.success) {
        setPreSelectedMember(response.data);
        if (shouldSkipSearch) {
          setSelectedMember(response.data);
        }
      }
    } catch (err) {
      console.error('Failed to load profile member:', err);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const response = await memberService.searchMembers(query, 10);
      if (response.success) {
        // Filter out members who already have a relationship, and the profile itself
        const existingIds = existingRelationships.map(r => r.relatedMember?.memberID);
        const filtered = response.data.filter(m =>
          !existingIds.includes(m.memberID) && m.memberID !== profileMemberId
        );
        setSearchResults(filtered);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  const selectMember = (member) => {
    setSelectedMember(member);
    setStep(2);
    setError('');
  };

  const handleSave = async () => {
    if (!selectedTypeId) {
      setError('Please select a relationship type.');
      return;
    }

    try {
      setSaving(true);
      setError('');

      let relatedMemberId;
      let onBehalfOfMemberID = null;

      if (isManagingProfile) {
        // Manager adding a relationship on behalf of the managed profile
        relatedMemberId = selectedMember.memberID;
        onBehalfOfMemberID = profileMemberId;
      } else if (isViewingSomeoneElse) {
        // Regular user on someone else's profile - add THEM as our relationship
        relatedMemberId = profileMemberId;
      } else {
        // Own profile - normal flow
        relatedMemberId = selectedMember.memberID;
      }

      const response = await relationshipService.createRelationship({
        relatedMemberID: relatedMemberId,
        relationshipTypeID: parseInt(selectedTypeId),
        notes: notes || null,
        onBehalfOfMemberID
      });

      if (response.success) {
        onAdded(response.data);
      } else {
        setError(response.message || 'Failed to create relationship.');
      }
    } catch (err) {
      setError('Failed to create relationship. It may already exist.');
      console.error('Failed to create relationship:', err);
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // Group types by category
  const groupedTypes = relationshipTypes.reduce((acc, type) => {
    if (!acc[type.category]) acc[type.category] = [];
    acc[type.category].push(type);
    return acc;
  }, {});

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content relationship-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {step === 1
              ? (isManagingProfile
                  ? `Add relationship for ${preSelectedMember?.firstName || 'this person'}`
                  : 'Select Family Member')
              : 'Choose Relationship'}
          </h3>
          <button className="btn btn-ghost" onClick={onClose}><FiX /></button>
        </div>

        <div className="modal-body">
          {step === 1 ? (
            <>
              <div className="search-input-wrapper">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search by name..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="member-search-results">
                {searching ? (
                  <div className="search-loading">
                    <span className="spinner spinner-sm"></span> Searching...
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map(member => (
                    <button
                      key={member.memberID}
                      className="member-search-item"
                      onClick={() => selectMember(member)}
                    >
                      {member.profilePhotoURL ? (
                        <img src={member.profilePhotoURL} alt={member.fullName} className="avatar avatar-sm" />
                      ) : (
                        <div className="avatar avatar-sm">
                          {getInitials(member.firstName, member.lastName)}
                        </div>
                      )}
                      <span>{member.fullName}</span>
                    </button>
                  ))
                ) : searchQuery.length >= 2 ? (
                  <p className="no-results">No members found</p>
                ) : (
                  <p className="search-hint">Type at least 2 characters to search</p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="selected-member-banner">
                {selectedMember?.profilePhotoURL ? (
                  <img src={selectedMember.profilePhotoURL} alt={selectedMember.fullName} className="avatar avatar-sm" />
                ) : (
                  <div className="avatar avatar-sm">
                    {getInitials(selectedMember?.firstName, selectedMember?.lastName)}
                  </div>
                )}
                <span>
                  {isManagingProfile
                    ? `${selectedMember?.fullName} is ${preSelectedMember?.firstName || 'their'}'s...`
                    : `${selectedMember?.fullName} is my...`}
                </span>
                {!shouldSkipSearch && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setStep(1)}>Change</button>
                )}
              </div>

              {error && <div className="form-error-message">{error}</div>}

              <div className="relationship-type-picker">
                {Object.entries(groupedTypes).map(([category, types]) => (
                  <div key={category} className="type-category">
                    <h4 className="type-category-label">{category}</h4>
                    <div className="type-options">
                      {types.map(type => (
                        <button
                          key={type.relationshipTypeID}
                          className={`type-option ${selectedTypeId == type.relationshipTypeID ? 'selected' : ''}`}
                          onClick={() => setSelectedTypeId(type.relationshipTypeID)}
                        >
                          {type.relationshipName}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Married since 2005"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        {step === 2 && (
          <div className="modal-footer">
            {!shouldSkipSearch && (
              <button className="btn btn-ghost" onClick={() => setStep(1)}>Back</button>
            )}
            <button className="btn btn-primary" onClick={handleSave} disabled={saving || !selectedTypeId || !selectedMember}>
              {saving ? 'Saving...' : 'Add Relationship'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Relationships;